import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, ReplaySubject } from 'rxjs';

import { BadgingService } from 'app/modules/social/services/badging/badging.service';
import { Loggable } from 'app/utils/logging/loggable';
import BadgeQuestion from 'entity/BadgeQuestion';
import SocialEntryConfiguration from 'entity/SocialEntryConfiguration';
import SocialRoomSet from 'entity/SocialRoomSet';
import EntryFlowType from 'enums/EntryFlowType';
import EntryOptionType from 'enums/EntryOptionType';

import { ScootaversePageService } from '../../services/scootaverse-page.service';

import { ScootaverseEntryRulesService } from './../../services/scootaverse-entry-rules.service';
import { Video } from '@playwright/test';
import { VideoChatService } from 'app/modules/social/services/videochat.service';

@Component({
  selector: 'entry-flow-details',
  templateUrl: './entry-flow-details.component.html',
  styleUrls: ['./entry-flow-details.component.scss'],
  standalone: false,
})
export class EntryFlowDetailsComponent extends Loggable implements OnDestroy, OnInit {
  private destroyed$ = new ReplaySubject<boolean>(1);
  //General Info
  public scootaverseId?: string;
  public entryFlowId?: string;
  public scootaverse?: SocialRoomSet;
  public loading = false;
  public defaultSettings: SocialEntryConfiguration = {
    name: '',
    entryOption: EntryOptionType.DEFAULT,
    startingPointX: 0,
    startingPointY: 0,
    groupFormerCount: 2,
    entryFlow: EntryFlowType.SPIRAL,
    rules: [],
  };
  public settings: SocialEntryConfiguration = this.defaultSettings;

  // Default Page Info
  public actualPage = 'default';
  public maxGroupSize = 0;
  public hasAssignedEntryFlow = false;
  public showBack = false;

  // Group Formers Page Info
  public roomBadgeQuestions: BadgeQuestion[] = [];

  // Apply Rooms Info
  public appliedRoomsName: string[] = [];
  public hasStartingPoint = false;

  public constructor(
    private route: ActivatedRoute,
    private scootaversePageService: ScootaversePageService,
    private scootaverseEntryRulesService: ScootaverseEntryRulesService,
    private badgingService: BadgingService,
    private toastrService: ToastrService,
    private videoChatService: VideoChatService,
  ) {
    super();
    this.setDisplayName('EntryFlowDetailsComponent');
  }

  public ngOnInit(): void {
    this.loading = true;
    this.scootaverseId = this.route.snapshot.paramMap.get('id') || undefined;
    this.entryFlowId = this.route.snapshot.paramMap.get('flow-id') || undefined;
    if (!this.scootaverseId) {
      void this.scootaversePageService.redirectToHome();
    } else {
      if (this.entryFlowId) {
        this.loadFlowInfo(this.scootaverseId, this.entryFlowId);
      }
      this.loadScootaverseInfo(this.scootaverseId)
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    }
  }

  public loadFlowInfo(scootaverseId: string, flowId: string) {
    this.scootaverseEntryRulesService
      .getEntryConfiguration(scootaverseId, flowId)
      .then((settings) => {
        this.settings = settings;
      })
      .catch((err) => {
        this.toastrService.error('Error loading flow information. Try again later.');
        this.info('Error loading entry flow: ', err);
      });
  }

  public async loadScootaverseInfo(scootaverseId: string) {
    await this.videoChatService
      .getSocialRoomSetPromise(scootaverseId)
      .then((scootaverseInfo) => {
        this.scootaverse = scootaverseInfo;
        this.settings.name = this.scootaverse?.name;
      })
      .catch((err) => {
        this.error('Error loading scootaverse information: ', err);
        this.toastrService.error('Error loading scootaverse information. Try again later.');
        void this.scootaversePageService.redirectToHome();
      });

    await firstValueFrom(this.videoChatService.getSocialRoomSetSettings(scootaverseId))
      .then((roomSetSettings) => {
        if (roomSetSettings.setSettings && roomSetSettings.setSettings?.MAX_GROUP_SIZE) {
          this.maxGroupSize = roomSetSettings.setSettings.MAX_GROUP_SIZE as number;
        } else {
          this.maxGroupSize = 10;
        }
      })
      .catch((err) => {
        this.info('Error loading scootaverse settings information: ', err);
        this.toastrService.error('Error loading scootaverse settings information. Try again later.');
        void this.scootaversePageService.redirectToEntryFlowList();
      });

    await this.scootaverseEntryRulesService
      .getEntryConfigurations(scootaverseId)
      .then((entryConfigurationList) => {
        this.hasStartingPoint = entryConfigurationList.some(
          (config) => config.entryOption === EntryOptionType.STARTING_POINT,
        );
        this.hasAssignedEntryFlow = !!entryConfigurationList.length;
      })
      .catch((err) => {
        this.error('Error loading entry rules information: ', err);
        this.toastrService.error('Error loading entry rules information. Try again later.');
        void this.scootaversePageService.redirectToHome();
      });

    await this.badgingService
      .getBadgeQuestions(scootaverseId)
      .then((badgeQuestions) => {
        this.info('SAVE BADGING', badgeQuestions);
        this.roomBadgeQuestions = badgeQuestions;
      })
      .catch((err: unknown) => {
        this.info('Error retrieving badge questions: ', err);
        this.toastrService.error('Error retrieving badge questions. Try again later.');
        void this.scootaversePageService.redirectToEntryFlowList();
      });
  }

  public get hasRoomQuestions() {
    return !!this.roomBadgeQuestions.length;
  }

  public onChangeSettings(event: SocialEntryConfiguration) {
    this.settings = { ...this.settings, ...event };
  }

  public applySocials(event: string) {
    const idArrays = [event];
    this.settings.associatedSocialIds = idArrays;

    if (this.scootaverse?.socials) {
      this.appliedRoomsName = this.scootaverse.socials
        .filter((social) => social.socialId && idArrays.includes(social.socialId))
        .map((social) => social.name)
        .filter((name): name is string => name !== undefined);
    }

    this.info('apply room list', idArrays, this.appliedRoomsName);

    this.nextStep();
  }

  public nextStep(): void {
    this.loading = true;

    switch (this.actualPage) {
      case 'default':
        if (this.settings.entryOption !== EntryOptionType.CUSTOM_RULES) {
          this.actualPage = 'apply-rooms';
          this.showBack = true;
        } else {
          this.actualPage = 'group-former';
          this.showBack = true;
        }
        break;
      case 'group-former':
        this.actualPage = 'apply-rooms';
        this.showBack = true;
        break;
      case 'apply-rooms':
        //Clear unnecessary information and format data
        if (this.settings.entryOption === EntryOptionType.DEFAULT) {
          this.settings.startingPointX = undefined;
          this.settings.startingPointY = undefined;
          this.settings.rules = undefined;
          this.settings.groupFormerCount = undefined;
        } else if (this.settings.entryOption === EntryOptionType.CUSTOM_RULES) {
          this.settings.startingPointX = undefined;
          this.settings.startingPointY = undefined;
        } else if (this.settings.entryOption === EntryOptionType.STARTING_POINT) {
          this.settings.entryFlow = undefined;
          this.settings.rules = undefined;
          this.settings.groupFormerCount = undefined;
        }

        this.actualPage = 'preview';
        this.showBack = true;
        break;
      default:
        this.actualPage = 'default';
        this.showBack = false;
        break;
    }

    this.loading = false;
  }

  public saveFlow(): void {
    this.info('SAVING FLOW', this.settings);
    if (this.scootaverseId) {
      this.loading = true;
      if (!this.entryFlowId) {
        this.scootaverseEntryRulesService
          .createEntryConfiguration(this.scootaverseId, this.settings)
          .then(() => {
            this.toastrService.success('Entry flow saved successfully!');
            this.loading = false;
            void this.scootaversePageService.redirectToEntryFlowList(this.scootaverseId);
          })
          .catch((err) => {
            this.toastrService.error('Error saving entry flows details. Try again later');
            this.loading = false;
            this.info('Error saving entry flow: ', err);
          });
      } else {
        this.scootaverseEntryRulesService
          .updateEntryConfiguration(this.scootaverseId, this.settings)
          .then(() => {
            this.toastrService.success('Entry flow saved successfully!');
            this.loading = false;
            void this.scootaversePageService.redirectToEntryFlowList(this.scootaverseId);
          })
          .catch((err) => {
            this.toastrService.error('Error saving entry flows details. Try again later');
            this.loading = false;
            this.info('Error saving entry flow: ', err);
          });
      }
    }
  }

  public handleBack(): void {
    switch (this.actualPage) {
      case 'group-former':
        this.actualPage = 'default';
        this.showBack = false;
        break;
      case 'apply-rooms':
        if (this.settings.entryOption === EntryOptionType.CUSTOM_RULES) {
          this.actualPage = 'group-former';
          this.showBack = true;
        } else {
          this.actualPage = 'default';
          this.showBack = false;
        }

        break;
      case 'preview':
        this.actualPage = 'apply-rooms';
        this.showBack = true;
        break;
      default:
        this.actualPage = 'default';
        this.showBack = false;
        break;
    }
  }

  public ngOnDestroy(): void {
    this.loading = false;
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
